import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Layout
import Sidebar from './components/Layout/Sidebar';
import Header  from './components/Layout/Header';

// Pages
import Login           from './pages/Login';
import Dashboard       from './pages/Dashboard';
import EmployeeList    from './pages/Employees/EmployeeList';
import CreateEmployee  from './pages/Employees/CreateEmployee';
import EmployeeProfile from './pages/Employees/EmployeeProfile';
import AlertsPage      from './pages/Alerts/AlertsPage';
import AuditReadiness  from './pages/Audit/AuditReadiness';
import TrainingPage    from './pages/Training/TrainingPage';
import ActivityLogs    from './pages/Logs/ActivityLogs';

// Candidate pages
import CandidateList    from './pages/Candidates/CandidateList';
import CreateCandidate  from './pages/Candidates/CreateCandidate';
import CandidateProfile from './pages/Candidates/CandidateProfile';

// I-9 pages
import I9List from './pages/I9/I9List';

// Reports
import ComplianceDashboard from './pages/Reports/ComplianceDashboard';

// Public pages (no auth / no sidebar)
import PublicDocUpload from './pages/Candidates/PublicDocUpload';
import PublicI9Portal  from './pages/I9/PublicI9Portal';

// Loading spinner while restoring auth state
function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full spinner" />
        <p className="text-slate-500 text-sm">Loading…</p>
      </div>
    </div>
  );
}

// Protects all routes that require authentication
function ProtectedLayout() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6 page-enter">
          <Routes>
            <Route path="/"                        element={<Dashboard />} />
            <Route path="/employees"               element={<EmployeeList />} />
            <Route path="/employees/create"        element={<CreateEmployee />} />
            <Route path="/employees/:id"           element={<EmployeeProfile />} />
            <Route path="/employees/:id/documents" element={<RedirectToProfile />} />
            <Route path="/alerts"                  element={<AlertsPage />} />
            <Route path="/audit"                   element={<AuditReadiness />} />
            <Route path="/training"                element={<TrainingPage />} />
            <Route path="/logs"                    element={<ActivityLogs />} />
            <Route path="/candidates"              element={<CandidateList />} />
            <Route path="/candidates/create"       element={<CreateCandidate />} />
            <Route path="/candidates/:id"          element={<CandidateProfile />} />
            <Route path="/i9"                      element={<I9List />} />
            <Route path="/compliance-dashboard"    element={<ComplianceDashboard />} />
            <Route path="*"                        element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function RedirectToProfile() {
  const { id } = useParams();
  return <Navigate to={`/employees/${id}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login"         element={<LoginGuard />} />
          <Route path="/upload/:token" element={<PublicDocUpload />} />
          <Route path="/i9/:token"     element={<PublicI9Portal />} />
          <Route path="/*"             element={<ProtectedLayout />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Redirect already-authenticated users away from /login
function LoginGuard() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <Login />;
}
